import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ComponentRef,
  ChangeDetectorRef,
} from '@angular/core';
import { AppService } from '@app/app.service';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { OrganizationInviteMemberModel, OrganizationMemberInvitationModel, OrganizationModel } from '@app/data-layer/models/organizations';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations/organization-member-model';
import { UserModel } from '@app/data-layer/models/user';
import { GenericService } from '@services/generic.service';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { OrganizationMemberInviteFormComponent } from '../organization-member-invite-form/organization-member-invite-form.component';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';

@Component({
  selector: 'organization-members-card',
  templateUrl: './organization-members-card.component.html',
  styleUrls: ['./organization-members-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationMembersCardComponent implements OnInit, OnDestroy {
  public isTablet$: Observable<boolean>;
  public isMobileMode$: Observable<boolean>;
  public enumOrganizationRole: typeof enumOrganizationRole = enumOrganizationRole;

  @Input() public organization: OrganizationModel;
  @Input() public currentUser: UserModel;
  @Input() public organizationMembers: OrganizationMemberModel[];
  @Input() public organizationInvitedMembers: OrganizationMemberInvitationModel[];

  @Output() public deleteOrganizationMember: EventEmitter<number> = new EventEmitter<number>();
  @Output() public inviteOrganizationMember: EventEmitter<OrganizationMemberInvitationModel[]> = new EventEmitter<
    OrganizationMemberInvitationModel[]
  >();
  @Output() public resendOrganizationMemberInvite: EventEmitter<number> = new EventEmitter<number>();
  @Output() public updateOrganizationMember: EventEmitter<OrganizationMemberInvitationModel[]> = new EventEmitter<
    OrganizationMemberInvitationModel[]
  >();
  @Output() public deleteOrganizationMemberInvite: EventEmitter<number> = new EventEmitter<number>();

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private currentUserMember: OrganizationMemberModel;

  constructor(
    private organizationsRepository: OrganizationsRepository,
    private genericService: GenericService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.isMobileMode$ = this.genericService.isTablet$;
    this.isTablet$ = this.genericService.isTablet$;
    this.currentUserMember = this.organizationMembers?.find((member) => member.user.userId === this.currentUser.uid);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get uniqueOrganizationMembers(): OrganizationMemberModel[] {
    return this.organizationMembers?.filter((member, index, self) => {
      return index === self.findIndex((m) => m.user.userId === member.user.userId);
    });
  }

  public get uniqueOrganizationInvitedMembers(): OrganizationMemberInvitationModel[] {
    return this.organizationInvitedMembers?.filter((member, index, self) => {
      return index === self.findIndex((m) => m.userEmail === member.userEmail);
    });
  }

  public getOrganizationMember(member: OrganizationMemberModel): OrganizationMemberModel {
    return member;
  }

  public get isAdmin(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.ADMIN;
  }

  public isMemberEditAllowed(member: OrganizationMemberModel): boolean {
    return this.isAdmin && member.user.userId !== this.currentUserMember.user.userId;
  }

  public isInviteEditAllowed(): boolean {
    return this.isAdmin;
  }

  public getOrganizationMembersCompanyIds(userId: string): number[] {
    const memberDuplicates = this.organizationMembers?.filter((m) => m.user.userId === userId);
    return memberDuplicates.map((member) => member.company.id);
  }

  public getOrganizationMembersCompanyNames(member: OrganizationMemberModel): string {
    const userId = member?.user?.userId;
    const memberDuplicates = this.organizationMembers?.filter((m) => m.user.userId === userId);
    const memberSlicedDuplicates = memberDuplicates.slice(0, 3);
    return member?.role === enumOrganizationRole.ADMIN
      ? 'LABELS.ALL'
      : memberSlicedDuplicates.map((m) => m.company.name).join(', ') + (memberDuplicates.length > 3 ? '...' : '');
  }

  public getOrganizationInvitedMembersCompanyNames(member: OrganizationMemberInvitationModel): string {
    const invitedSlicedCompanies = member?.companies.slice(0, 3);
    return member?.role === enumOrganizationRole.ADMIN
      ? 'LABELS.ALL'
      : invitedSlicedCompanies.map((c) => c.name).join(', ') + (member?.companies.length > 3 ? '...' : '');
  }

  public openInviteSideBar(member?: OrganizationMemberModel) {
    const componentSidebarRef: ComponentRef<OrganizationMemberInviteFormComponent> = this.appService.openSidebar(
      OrganizationMemberInviteFormComponent,
      {
        sidebarHeaderText: member ? 'BUTTON.EDIT_PERMISSIONS' : 'LABELS.INVITE_MEMBERS',
      },
    );

    //Passing props to OrganizationMemberInviteFormComponent instance
    componentSidebarRef.instance.selectedCompaniesIds = !!member ? this.getOrganizationMembersCompanyIds(member.user.userId) : [];
    componentSidebarRef.instance.acceptedMembers = this.organizationMembers;
    componentSidebarRef.instance.currentMember = member;
    componentSidebarRef.instance.companies = this.organization.companies;

    componentSidebarRef.instance.sendInviteSource
      .pipe(
        switchMap((data: OrganizationInviteMemberModel) =>
          this.organizationsRepository.inviteOrganizationMember(this.organization.id, data),
        ),
        switchMap(() => {
          return this.organizationsRepository.getOrganizationInvitedMembers(this.organization.id);
        }),
        take(1),
      )
      .subscribe((organizationInvitedMembers: OrganizationMemberInvitationModel[]) => {
        this.inviteOrganizationMember.emit(organizationInvitedMembers);
        this.cdr.detectChanges();
        this.appService.closeSidebar();
      });

    componentSidebarRef.instance.updateInviteSource
      .pipe(
        switchMap((response) =>
          this.organizationsRepository.updateOrganizationMember(this.organization.id, response.member.user.userId, response.data),
        ),
        switchMap(() => {
          return this.organizationsRepository.getOrganizationMembers(this.organization.id);
        }),
        take(1),
      )
      .subscribe((organizationMembers: OrganizationMemberModel[]) => {
        this.updateOrganizationMember.emit(organizationMembers);
        this.cdr.detectChanges();
        this.appService.closeSidebar();
      });
  }

  public resendInvite(invitationId: number) {
    this.resendOrganizationMemberInvite.emit(invitationId);
  }

  public removeInvite(invitationId: number) {
    this.deleteOrganizationMemberInvite.emit(invitationId);
  }
}
